import moment from "moment";
import { jsPDF } from "jspdf";
import JSZip from "jszip";
import numWords from "num-words";

import {
  capitalizeFirstLetter,
  downloadFile,
  getImage,
  getLocalisedValue,
  wait,
} from "@/app/core-module/util/coreHelper";
import {
  splineRegular,
  splineMedium,
  splineSemiBold,
  splineBold,
} from "@/app/core-module/util/spline-font";
import { getInvoiceTaxQrCode } from "@/app/invoices-module/helpers/invoiceTaxHelper";

const generateTrainingRegistrationInvoicePdf = ({
  trainingRegistrationInvoices,
  subscriberInfo,
  download = false,
  debug = false,
}) => {
  try {
    if (!trainingRegistrationInvoices?.length) return;

    //region jspdf doc init
    const doc = new jsPDF({
      orientation: "p",
      format: "a4",
      compress: true,
    });

    //region font setup
    doc.addFileToVFS("SplineSans-Regular-normal.ttf", splineRegular);
    doc.addFont(
      "SplineSans-Regular-normal.ttf",
      "SplineSans-Regular",
      "normal"
    );

    doc.addFileToVFS("SplineSans-Medium-normal.ttf", splineMedium);
    doc.addFont("SplineSans-Medium-normal.ttf", "SplineSans-Medium", "normal");

    doc.addFileToVFS("SplineSans-SemiBold-normal.ttf", splineSemiBold);
    doc.addFont(
      "SplineSans-SemiBold-normal.ttf",
      "SplineSans-SemiBold",
      "normal"
    );

    doc.addFileToVFS("SplineSans-Bold-bold.ttf", splineBold);
    doc.addFont("SplineSans-Bold-bold.ttf", "SplineSans-Bold", "bold");
    //endregion
    //endregion

    //region utility functions
    const setFontType = (type, weight) => {
      if (type === "bold" || weight === 700) {
        doc.setFont("SplineSans-Bold", "bold");
      } else if (type === "semi-bold" || weight === 600) {
        doc.setFont("SplineSans-SemiBold", "normal");
      } else if (type === "medium" || weight === 500) {
        doc.setFont("SplineSans-Medium", "normal");
      } else {
        //type=regular
        //weight=400
        doc.setFont("SplineSans-Regular", "normal");
      }
    };

    const trimText = (value, maxLength, maxLines) => {
      return doc.splitTextToSize(value, maxLength).slice(0, maxLines);
    };

    const getImageInfo = (image, width, height, { fit } = {}) => {
      height = height ?? width;
      width = width ?? height;
      let imageInfo, imageHeight, imageWidth, imageAspectRatio;

      if (fit != null && image != null) {
        imageInfo = doc.getImageProperties(image);
        imageHeight = imageInfo?.height ?? height;
        imageWidth = imageInfo?.width ?? width;
        imageAspectRatio = imageHeight / imageWidth;

        if (fit === "WIDTH" && width != null) {
          height = width * imageAspectRatio;
        } else if (fit === "HEIGHT" && height != null) {
          width = height / imageAspectRatio;
        }
      }

      return {
        width,
        height,
        widthOg: imageWidth,
        heightOg: imageHeight,
        aspectRatio: imageAspectRatio,
        fileType: imageInfo?.fileType?.toLowerCase() ?? "jpeg",
      };
    };

    const addImage = (image, x, y, width, height, { fit } = {}) => {
      const imageInfo = getImageInfo(image, width, height, { fit });

      if (image != null) {
        doc.addImage(
          image,
          imageInfo.fileType,
          x,
          y,
          imageInfo.width,
          imageInfo.height
        );
      }

      return { x, y, ...imageInfo };
    };
    //endregion

    const subscriberLogo = getImage(subscriberInfo?.user?.avatar, {
      params: `?r=${Math.random()}`,
    });

    const subscriberAddress = getLocalisedValue(
      subscriberInfo?.basicInfo?.address,
      { preferredLocale: "en" }
    );
    const subscriberPhone = subscriberInfo?.user?.phone?.number ?? "";
    const subscriberAlternatePhone =
      subscriberInfo?.basicInfo?.alternatePhone ?? "";
    const subscriberEmail = subscriberInfo?.user?.email ?? "";
    const subscriberWebsite = subscriberInfo?.basicInfo?.website ?? "";
    const termsAndConditions =
      "We declare that this invoice show the actual price of the goods and services described and that all particulars are true and correct";

    const addPageContent = trainingRegistrationInvoice => {
      //region process data from trainingRegistrationInvoice
      const organizationName = getLocalisedValue(
        trainingRegistrationInvoice.organizationDetails?.organizationName,
        { preferredLocale: "en" }
      );
      const organizationPhone =
        trainingRegistrationInvoice.organizationDetails?.phone ?? "";
      const organizationEmail =
        trainingRegistrationInvoice.organizationDetails?.email ?? "";

      const invoiceNo = trainingRegistrationInvoice.invoiceNo ?? "";
      const invoiceDate = trainingRegistrationInvoice.invoiceDate
        ? moment(parseInt(trainingRegistrationInvoice.invoiceDate)).format(
            "DD MMMM YYYY"
          )
        : "";
      const buyerOrderNo = trainingRegistrationInvoice.buyerOrderNo ?? "";
      const buyerOrderDate = trainingRegistrationInvoice.buyerOrderDate
        ? moment(parseInt(trainingRegistrationInvoice.buyerOrderDate)).format(
            "DD MMMM YYYY"
          )
        : "";

      const currency = trainingRegistrationInvoice.currency ?? "";
      let invoiceQuantity = 0;
      let invoiceAmount = 0;

      const orderItems = trainingRegistrationInvoice.orderItems?.map(
        (x, index) => {
          const quantity = x.quantity ?? 0;
          const rate = (x.unitPrice ?? 0) - (x.discount ?? 0);
          const amount = quantity * rate;

          invoiceQuantity += quantity;
          invoiceAmount += amount;

          return {
            serialNo: index + 1,
            trainingTitle: getLocalisedValue(x.trainingTitle, {
              preferredLocale: "en",
            }),
            quantity,
            rate,
            amount,
          };
        }
      );

      const bankDetails = {
        accountName: trainingRegistrationInvoice.bankDetails?.accountName ?? "",
        bankName: trainingRegistrationInvoice.bankDetails?.bankName ?? "",
        accountNumber:
          trainingRegistrationInvoice.bankDetails?.accountNumber ?? "",
        branch: trainingRegistrationInvoice.bankDetails?.branch ?? "",
        ifsc: trainingRegistrationInvoice.bankDetails?.ifsc ?? "",
      };

      const termsOfDelivery = trainingRegistrationInvoice.termsOfDelivery ?? "";
      const paymentMode = trainingRegistrationInvoice.paymentMode ?? "";
      const deliveryNote = trainingRegistrationInvoice.deliveryNote ?? "";
      //endregion

      const data = {
        invoiceTitle: "INVOICE",
        invoiceToTitle: "INVOICE TO",
        organizationName,
        panOrItNoTitle: "PAN/IT No.",
        panOrItNo: "",
        contactPersonTitle: "Contact Person",
        phoneTitle: "Phone",
        organizationPhone,
        emailTitle: "Email",
        organizationEmail,
        invoiceNoTitle: "Invoice No",
        invoiceNo,
        invoiceDateTitle: "Invoice Date",
        invoiceDate,
        buyerOrderNoTitle: "Buyer's Order No",
        buyerOrderNo,
        buyerOrderDateTitle: "Buyer Date",
        buyerOrderDate,
        tableTrainingTitle: "Course",
        tableQuantityTitle: "Quantity",
        tableRateTitle: "Rate",
        tableAmountTitle: "Amount",
        orderItems,
        totalTitle: "TOTAL",
        currency,
        invoiceQuantity,
        invoiceAmount,
        amountInWordsTitle: "Amount in words",
        amountInWords: `${capitalizeFirstLetter(
          numWords(invoiceAmount.toFixed())
        )} only`,
        bankDetailsTitle: "BANK DETAILS",
        bankDetails: {
          accountNameTitle: "Account name",
          accountName: bankDetails.accountName.toUpperCase(),
          bankNameTitle: "Bank name",
          bankName: bankDetails.bankName.toUpperCase(),
          accountNumberTitle: "Account number",
          accountNumber: bankDetails.accountNumber.toUpperCase(),
          branchTitle: "Branch name",
          branch: bankDetails.branch.toUpperCase(),
          ifscTitle: "IFSC",
          ifsc: bankDetails.ifsc.toUpperCase(),
        },
        termsOfDeliveryTitle: "Terms of delivery",
        termsOfDelivery,
        paymentModeTitle: "Mode of payment",
        paymentMode,
        deliveryNoteTitle: "Delivery note",
        deliveryNote,
        termsAndConditionsTitle: "Terms & Conditions",
        termsAndConditions,
        customerSealAndSignatureTitle: "Customer's Seal and Signature",
      };

      //region pdf default variables and constants
      const defaultFontSize = 10;
      const smallFontSize = 13;
      const lineHeight = Math.floor(defaultFontSize / 2);
      let currentY = lineHeight;
      let pageNumber = 1;
      const maxCharPerLine = 185;
      const pageWidth = 197; //192
      const pageHeight = 280;
      const center = pageWidth / 2;
      const start = 14;
      const end = pageWidth;
      let prevRowCount = 2;
      let prevRowEnd = 0;
      //endregion

      let printBottomPartOnNextPage = false;

      const printInvoice = () => {
        currentY = lineHeight;
        setFontType("regular");
        printHeader(4);
        printInfo();
        printTableHeaders();

        // debugger;
        setFontType("regular");
        data.orderItems.forEach((orderItem, index) => {
          printRow(orderItem, index + 1);

          if (currentY > pageHeight - 5) {
            gotoNextPage();
          }

          printBottomPartOnNextPage = currentY > pageHeight - 100;
        });

        if (printBottomPartOnNextPage) {
          gotoNextPage();
        }

        printTableTotal();
        printTableFooter();
        printBankDetails();
        printDeliveryDetails();
        printTermsAndCondition();
        printPageFooter();
      };

      const printHeader = (extraY = 0) => {
        const { width } = addImage(subscriberLogo, start, 0, 25, 28, {
          fit: "HEIGHT",
        });

        /*addImage(
          getInvoiceTaxQrCode(trainingRegistrationInvoice, 100),
          start + (width ?? 25) + 20,
          0,
          28,
          28
        );*/

        doc.setFontSize(30);
        doc.setTextColor(40, 17, 102);
        setFontType("bold");
        doc.text(data.invoiceTitle, end, currentY + 10 + extraY, {
          align: "right",
        });
      };

      const printInfo = () => {
        //region organization name
        gotoNextLine(2);
        doc.setTextColor(40, 17, 102);
        doc.setFontSize(12);
        gotoNextLine(5);

        doc.setFontSize(10);
        doc.text(data.invoiceToTitle, start, currentY);

        gotoNextLine(1.5);
        doc.setFontSize(16);
        // INFO: For testing line length
        // doc.line(start, currentY, start + 100, currentY);
        const organizationNameSplit = trimText(data.organizationName, 80, 2);
        doc.text(organizationNameSplit, start, currentY);
        //endregion

        //region invoice no & date
        const invoiceResponsiveVerticalSpace =
          organizationNameSplit.length > 1 ? lineHeight : 0;
        doc.setFontSize(10);
        setFontType("regular");
        const invoiceNoMaxWidth = Math.max(
          doc.getTextWidth(data.invoiceNo),
          doc.getTextWidth(data.invoiceDate),
          doc.getTextWidth(data.buyerOrderNo),
          doc.getTextWidth(data.buyerOrderDate)
        );
        // INFO: For testing line length
        // doc.line(start, currentY, end - invoiceNoMaxWidth, currentY);
        doc.text(
          `${data.invoiceNoTitle}   :`,
          end - invoiceNoMaxWidth - 2,
          currentY + invoiceResponsiveVerticalSpace,
          { align: "right" }
        );
        doc.text(
          data.invoiceNo,
          end,
          currentY + invoiceResponsiveVerticalSpace,
          {
            align: "right",
          }
        );

        gotoNextLine(1);
        doc.text(
          `${data.invoiceDateTitle}   :`,
          end - invoiceNoMaxWidth - 2,
          currentY + invoiceResponsiveVerticalSpace,
          { align: "right" }
        );
        doc.text(
          data.invoiceDate,
          end,
          currentY + invoiceResponsiveVerticalSpace,
          { align: "right" }
        );
        //endregion

        //region pan no
        setFontType("medium");
        const panResponsiveVerticalSpace =
          organizationNameSplit.length < 2 ? lineHeight : 0;
        doc.setFontSize(8);
        gotoNextLine(1.2);
        doc.text(
          `${data.panOrItNoTitle}       :   ${data.panOrItNo}`,
          start,
          currentY - panResponsiveVerticalSpace
        );
        doc.setDrawColor(147, 136, 179);
        doc.line(
          start,
          currentY + 4 - panResponsiveVerticalSpace,
          start + 80,
          currentY + 4 - panResponsiveVerticalSpace
        );
        //endregion

        //region contact phone, email, buyer invoice no & date
        gotoNextLine(2);
        doc.setFontSize(10);
        doc.text(data.contactPersonTitle, start, currentY);

        setFontType("regular");
        doc.setFontSize(10);
        gotoNextLine(1);
        doc.text(data.phoneTitle, start, currentY);
        doc.text(`:   ${data.organizationPhone}`, start + 12, currentY);

        doc.text(
          `${data.buyerOrderNoTitle}   :`,
          end - invoiceNoMaxWidth - 2,
          currentY,
          { align: "right" }
        );
        doc.text(data.buyerOrderNo, end, currentY, { align: "right" });

        gotoNextLine(1);
        doc.text(data.emailTitle, start, currentY);
        doc.text(`:   ${data.organizationEmail}`, start + 12, currentY);

        doc.text(
          `${data.buyerOrderDateTitle}    :`,
          end - invoiceNoMaxWidth - 2,
          currentY,
          { align: "right" }
        );
        doc.text(data.buyerOrderDate, end, currentY, {
          align: "right",
        });
        //endregion
      };

      const printTableHeaders = () => {
        gotoNextLine(2);
        doc.rect(start, currentY, pageWidth - 14, 11, "F");
        gotoNextLine(1.3);
        doc.setTextColor(255, 255, 255);
        setFontType("semi-bold");

        doc.text("#", start + 4, currentY);
        doc.text(data.tableTrainingTitle, start + 12, currentY);
        doc.text(data.tableQuantityTitle, start + 100, currentY);
        doc.text(data.tableRateTitle, start + 130, currentY);
        doc.text(data.tableAmountTitle, start + 160, currentY);
      };

      const printRow = (orderItem, index) => {
        gotoNextLine(prevRowCount);

        doc.setDrawColor(245, 246, 250);
        doc.setFillColor(245, 246, 250);
        let mulFactor = 0;

        const trainingTitleMaxLength = 80;
        const splitTitle = doc.splitTextToSize(
          orderItem.trainingTitle,
          trainingTitleMaxLength
        );
        const requiredNumberOfLines = splitTitle.length + 1;

        if (index % 2 === 0) {
          let fill = "FD";
          if (splitTitle.length > 1) {
            mulFactor = 0.5;
          }
          doc.rect(
            start,
            currentY - 5,
            pageWidth - 14,
            7 * (splitTitle.length - mulFactor),
            fill
          );
        }

        prevRowCount = splitTitle.length + 0.5;

        doc.setTextColor(147, 136, 179);
        doc.text(String(index), start + 4, currentY);

        doc.setTextColor(40, 17, 102);
        // INFO: For testing line length
        // doc.line(start + 12, currentY, start + 12 + 80, currentY);
        doc.text(orderItem.trainingTitle, start + 12, currentY, {
          maxWidth: trainingTitleMaxLength,
          lineHeightFactor: 1,
        });
        // INFO: For testing line length
        // doc.line(start + 100, currentY, start + 100 + 20, currentY);
        doc.text(orderItem.quantity.toString(), start + 101, currentY);
        // INFO: For testing line length
        // doc.line(start + 130, currentY, start + 130 + 20, currentY);
        doc.text(orderItem.rate.toString(), start + 131, currentY);
        // INFO: For testing line length
        // doc.line(start + 160, currentY, start + 160 + 20, currentY);
        doc.text(orderItem.amount.toString(), start + 161, currentY);
      };

      const printTableTotal = () => {
        gotoNextLine(2);
        setFontType("semi-bold");
        doc.setTextColor(40, 17, 102);
        doc.text(data.totalTitle, start + 60, pageHeight - 95);
        setFontType("regular");
        doc.text(data.invoiceQuantity.toString(), start + 101, pageHeight - 95);
        setFontType("semi-bold");
        doc.text(data.invoiceAmount.toString(), start + 161, pageHeight - 95);
      };

      const printTableFooter = () => {
        gotoNextLine(2);
        doc.setFillColor(200, 194, 217);
        doc.rect(start, pageHeight - 90, pageWidth / 2 - 6, 12, "F");
        doc.setFillColor(40, 17, 102);
        doc.rect(
          pageWidth / 2 + 10,
          pageHeight - 90,
          pageWidth / 2 - 10,
          12,
          "F"
        );

        doc.setTextColor(40, 17, 102);
        doc.setFontSize(12);
        setFontType("semi-bold");
        doc.text(data.amountInWordsTitle, start + 5.5, pageHeight - 90 + 7.5);

        setFontType("bold");
        doc.setTextColor(255, 255, 255);
        doc.text(data.totalTitle, pageWidth / 2 + 16, pageHeight - 90 + 7.5);
        doc.text(
          `${data.currency} ${data.invoiceAmount.toFixed()}`,
          end - 12,
          pageHeight - 90 + 7.5,
          { align: "right" }
        );

        doc.setFontSize(8);
        setFontType("regular");
        doc.setTextColor(40, 17, 102);
        const amountInWordsSplit = trimText(data.amountInWords, 40, 3);
        doc.text(
          amountInWordsSplit,
          start + 70,
          pageHeight -
            80 -
            (amountInWordsSplit.length < 2 ? 3 : 3 + amountInWordsSplit.length),
          { align: "center" }
        );
      };

      const printBankDetails = () => {
        doc.setTextColor(40, 17, 102);
        doc.setFontSize(10);
        gotoNextLine(4);
        setFontType("bold");
        doc.text(data.bankDetailsTitle, start, pageHeight - 68);

        setFontType("regular");
        doc.text(data.bankDetails.accountNameTitle, start, pageHeight - 62);
        doc.text(
          `:   ${data.bankDetails.accountName}`,
          start + 29,
          pageHeight - 62
        );

        doc.text(data.bankDetails.bankNameTitle, start, pageHeight - 57);
        doc.text(
          `:   ${data.bankDetails.bankName}`,
          start + 29,
          pageHeight - 57
        );

        doc.text(data.bankDetails.accountNumberTitle, start, pageHeight - 52);
        doc.text(
          `:   ${data.bankDetails.accountNumber}`,
          start + 29,
          pageHeight - 52
        );

        doc.text(data.bankDetails.branchTitle, start, pageHeight - 47);
        doc.text(`:   ${data.bankDetails.branch}`, start + 29, pageHeight - 47);

        doc.text(data.bankDetails.ifscTitle, start, pageHeight - 42);
        doc.text(`:   ${data.bankDetails.ifsc}`, start + 29, pageHeight - 42);
      };

      const printDeliveryDetails = () => {
        let yAxisDeliveryDetails = pageHeight - 62;

        //region invoice no & date
        const fontSizeDeliveryDetails = 8;
        const lineHeightDeliveryDetails = fontSizeDeliveryDetails / 2;

        doc.setFontSize(fontSizeDeliveryDetails);

        const termsOfDeliveryMaxWidth = 40;
        // INFO: For testing line length
        // doc.setDrawColor(0, 0, 0);
        // doc.line(
        //   end - termsOfDeliveryMaxWidth,
        //   yAxisDeliveryDetails,
        //   end,
        //   yAxisDeliveryDetails
        // );
        setFontType("medium");
        doc.text(
          `${data.termsOfDeliveryTitle}   :`,
          end - termsOfDeliveryMaxWidth - 2,
          yAxisDeliveryDetails,
          { align: "right" }
        );
        const termsOfDeliverySplit = trimText(
          data.termsOfDelivery,
          termsOfDeliveryMaxWidth,
          3
        );
        setFontType("regular");
        doc.text(
          termsOfDeliverySplit,
          end - termsOfDeliveryMaxWidth,
          yAxisDeliveryDetails
        );

        yAxisDeliveryDetails +=
          lineHeightDeliveryDetails * termsOfDeliverySplit.length;
        setFontType("medium");
        doc.text(
          `${data.paymentModeTitle}   :`,
          end - termsOfDeliveryMaxWidth - 2,
          yAxisDeliveryDetails,
          { align: "right" }
        );
        const paymentModeSplit = trimText(
          data.paymentMode,
          termsOfDeliveryMaxWidth,
          2
        );
        setFontType("regular");
        doc.text(
          paymentModeSplit,
          end - termsOfDeliveryMaxWidth,
          yAxisDeliveryDetails
        );

        yAxisDeliveryDetails +=
          lineHeightDeliveryDetails * paymentModeSplit.length;
        setFontType("medium");
        doc.text(
          `${data.deliveryNoteTitle}   :`,
          end - termsOfDeliveryMaxWidth - 2,
          yAxisDeliveryDetails,
          { align: "right" }
        );
        const deliveryNoteSplit = trimText(
          data.deliveryNote,
          termsOfDeliveryMaxWidth,
          5
        );
        setFontType("regular");
        doc.text(
          deliveryNoteSplit,
          end - termsOfDeliveryMaxWidth,
          yAxisDeliveryDetails
        );
        //endregion
      };

      const printTermsAndCondition = () => {
        gotoNextLine(3);
        doc.setFontSize(8);
        setFontType("semi-bold");
        doc.text(data.termsAndConditionsTitle, start, pageHeight - 32);

        gotoNextLine(1);
        setFontType("regular");
        doc.text(data.termsAndConditions, start, pageHeight - 27, {
          maxWidth: 60,
        });

        setFontType("semi-bold");
        doc.setFontSize(10);
        doc.text(data.customerSealAndSignatureTitle, end, pageHeight - 19, {
          align: "right",
        });
      };

      const printPageFooter = () => {
        // blue color rectangle at the bottom of page
        doc.line(0, pageHeight - 5, pageWidth + 20, pageHeight - 5);
        addImage(subscriberLogo, start, pageHeight, 5, 8, { fit: "HEIGHT" });

        //region subscriber address
        doc.setFontSize(8);

        const subscriberAddressSplit = trimText(subscriberAddress, 45, 2);
        const subscriberAddressMaxWidth = Math.min(
          Math.max(...subscriberAddressSplit.map(x => doc.getTextWidth(x))),
          45
        );

        const subscriberPhoneMaxWidth = Math.min(
          Math.max(
            doc.getTextWidth(subscriberPhone),
            doc.getTextWidth(subscriberAlternatePhone)
          ),
          45
        );

        const subscriberEmailMaxWidth = Math.min(
          Math.max(
            doc.getTextWidth(subscriberEmail),
            doc.getTextWidth(subscriberWebsite)
          ),
          45
        );

        doc.addImage(
          require("../assets/images/address-icon.png"),
          "png",
          end -
            subscriberEmailMaxWidth -
            20 -
            subscriberPhoneMaxWidth -
            15 -
            subscriberAddressMaxWidth -
            8,
          pageHeight + 1,
          6,
          7
        );

        setFontType("regular");
        // // INFO: For testing line length
        // doc.setDrawColor(0, 0, 0);
        // doc.line(
        //   end -
        //     subscriberEmailMaxWidth -
        //     20 -
        //     subscriberPhoneMaxWidth -
        //     15 -
        //     subscriberAddressMaxWidth,
        //   pageHeight + 4,
        //   end - subscriberEmailMaxWidth - 20 - subscriberPhoneMaxWidth - 20,
        //   pageHeight + 4
        // );
        doc.text(
          subscriberAddressSplit,
          end -
            subscriberEmailMaxWidth -
            20 -
            subscriberPhoneMaxWidth -
            15 -
            subscriberAddressMaxWidth,
          pageHeight + 4
        );
        //endregion

        //region subscriber phone & alternate phone
        doc.addImage(
          require("../assets/images/phone-icon.png"),
          "png",
          end - subscriberEmailMaxWidth - 20 - subscriberPhoneMaxWidth - 8,
          pageHeight + 1,
          6,
          7
        );
        // // INFO: For testing line length
        // doc.setDrawColor(0,0,0)
        // doc.line(
        //   end - subscriberEmailMaxWidth - 20 - subscriberPhoneMaxWidth,
        //   pageHeight + 4,
        //   end - subscriberEmailMaxWidth - 20,
        //   pageHeight + 4
        // );
        doc.text(
          trimText(subscriberPhone, subscriberPhoneMaxWidth, 1),
          end - subscriberEmailMaxWidth - 20 - subscriberPhoneMaxWidth,
          pageHeight + 4
        );
        doc.text(
          trimText(subscriberAlternatePhone, subscriberPhoneMaxWidth, 1),
          end - subscriberEmailMaxWidth - 20 - subscriberPhoneMaxWidth,
          pageHeight + 7
        );
        //endregion

        //region subscriber email & website
        doc.addImage(
          require("../assets/images/mail-icon.png"),
          "png",
          end - subscriberEmailMaxWidth - 10,
          pageHeight + 1,
          8,
          7
        );
        // // INFO: For testing line length
        // doc.setDrawColor(0,0,0)
        // doc.line(
        //   end - subscriberEmailMaxWidth,
        //   pageHeight + 4,
        //   end,
        //   pageHeight + 4
        // );
        doc.text(
          trimText(subscriberEmail, subscriberEmailMaxWidth, 1),
          end - subscriberEmailMaxWidth,
          pageHeight + 4
        );
        doc.text(
          trimText(subscriberWebsite, subscriberEmailMaxWidth, 1),
          end - subscriberEmailMaxWidth,
          pageHeight + 7
        );
        //endregion

        doc.rect(0, pageHeight + 14, pageWidth + 20, 4, "F");
      };

      const gotoNextLine = numberOfLines => {
        if (numberOfLines == null) numberOfLines = 1;
        currentY += numberOfLines * lineHeight;
        if (currentY > pageHeight) gotoNextPage();
      };

      const gotoNextPage = () => {
        doc.addPage();
        pageNumber++;
        currentY = 2 * lineHeight;
        doc.text("Page: " + pageNumber, end, currentY, { align: "right" });
        printHeader();
        printInfo();
        printTableHeaders();
        gotoNextLine(2);
        // doc.line(start, currentY + 2.5, end, currentY + 2.5)
      };

      printInvoice();
    };

    trainingRegistrationInvoices.forEach(
      (trainingRegistrationInvoice, index) => {
        addPageContent(trainingRegistrationInvoice);

        if (trainingRegistrationInvoices.length > index + 1) {
          doc.addPage();
          // lineHeight = Math.floor(normalFontSize / 2);
          // currentY = lineHeight;
        }
      }
    );

    if (debug) {
      window.open(doc.output("bloburl"), "_blank");
      return true;
    } else {
      if (download) {
        if (trainingRegistrationInvoices.length === 1) {
          return doc.save(
            `invoice-${
              trainingRegistrationInvoices[0].invoiceNo ?? "0"
            }-${Date.now()}`
          );
        } else {
          return doc.save(`invoices-${Date.now()}`);
        }
      } else {
        return doc.output("blob");
      }
    }
  } catch (e) {
    console.log(
      "invoiceHelper.generateTrainingRegistrationInvoicePdf.exception:",
      e
    );

    return false;
  }
};

export const bulkDownloadTrainingRegistrationInvoices = async ({
  trainingRegistrationInvoices,
  subscriberInfo,
  zip = false,
  debug = false,
}) => {
  try {
    if (!trainingRegistrationInvoices?.length) return;

    await wait(100);

    if (zip && trainingRegistrationInvoices.length > 1) {
      const zip = new JSZip();
      const invoices = zip.folder(`invoices-${Date.now()}`);

      trainingRegistrationInvoices.forEach(
        (trainingRegistrationInvoice, index) => {
          const blobData = generateTrainingRegistrationInvoicePdf({
            trainingRegistrationInvoices: [trainingRegistrationInvoice],
            subscriberInfo,
          });

          if (blobData) {
            invoices.file(
              `invoice-${
                trainingRegistrationInvoice.invoiceNo ?? index
              }-${Date.now()}.pdf`,
              blobData
            );
          }
        }
      );

      if (invoices?.files && Object.keys(invoices?.files).length) {
        return await zip
          .generateAsync({ type: "blob" })
          .then(data => {
            downloadFile({ data, fileName: `invoices-${Date.now()}.zip` });
            return true;
          })
          .catch(e => {
            console.log(
              "invoiceHelper.bulkDownloadTrainingRegistrationInvoices:zip.generateAsync:error:",
              e.message
            );

            return false;
          });
      }
    } else {
      return !!generateTrainingRegistrationInvoicePdf({
        trainingRegistrationInvoices,
        subscriberInfo,
        download: true,
        debug,
      });
    }
  } catch (e) {
    console.log(
      "invoiceHelper.bulkDownloadTrainingRegistrationInvoices.exception:",
      e.message
    );

    return false;
  }
};
